import { trackPromise } from "react-promise-tracker";

const inFlightRequests = new Map<string, Promise<Response>>();
const buildRequestKey = (url: string, options: RequestInit): string => {
    const { method = "GET", body } = options;
    const normalizedUrl = new URL(url, window.location.origin);
    const sortedParams = Array.from(normalizedUrl.searchParams.entries()).sort();
    const paramsString = JSON.stringify(sortedParams);

    let bodyString = "";
    if (body && typeof body === "string") bodyString = body;
    else if (body && typeof body === "object") bodyString = JSON.stringify(body);

    return `${method}:${normalizedUrl.toString()}?${paramsString}:${bodyString}`;
};

const safeAppendHeaders = (options: RequestInit = {}): RequestInit => {
    // If the body is FormData/etc., we should not set Content-Type header as
    // the browser will set it including the correct boundary.
    if (
        options.body instanceof FormData ||
        options.body instanceof File ||
        options.body instanceof Blob ||
        options.body instanceof ArrayBuffer ||
        options.body instanceof URLSearchParams
    ) {
        return options;
    }

    // If headers already include Content-Type or Accept, do not override it. We
    // assume that the caller knows what they are doing.
    if (
        options.headers &&
        typeof options.headers === "object" &&
        ("Content-Type" in options.headers || "Accept" in options.headers)
    ) {
        return options;
    }

    // Default headers for JSON APIs.
    const headers = {
        "Content-Type": "application/json",
        Accept: "application/json",
    };

    options.headers = {
        ...headers,
        ...options.headers,
    };
    return options;
};

export function plotFetch(url: string, options: RequestInit = {}, track: boolean = false): Promise<Response> {
    options = safeAppendHeaders(options);
    options.credentials = options.credentials || "include";

    const method = options.method?.toUpperCase() || "GET";

    if (method === "GET" || method === "HEAD" || method === "OPTIONS") {
        const key = buildRequestKey(url, options);
        const existing = inFlightRequests.get(key);
        if (existing) {
            return existing.then((res) => res.clone());
        }

        const fetchPromise = (track ? trackPromise(fetch(url, options)) : fetch(url, options))
            .then((res) => {
                inFlightRequests.delete(key);
                return res;
            })
            .catch((err) => {
                inFlightRequests.delete(key);
                throw err;
            });

        inFlightRequests.set(key, fetchPromise);
        return fetchPromise.then((res) => res.clone());
    } else {
        // For non-GET requests, we do not deduplicate for safety.
        return track ? trackPromise(fetch(url, options)) : fetch(url, options);
    }
}
